import { useEffect, useState, useCallback } from "react";
import { DataTable, ColumnDef, FilterOption } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Download } from "lucide-react";
import { api, ApiClientError } from "@/lib/api";

const PAGE_SIZE = 50;

// Frontend user display type (transformed from backend User)
interface UserDisplayData {
  id: string;
  name: string;
  email: string;
  agency: string;
  role: string;
  department: string;
  cases: number;
  last_active: string;
  status: string;
}

export function UserList() {
  const { logout } = useAuth();
  const [users, setUsers] = useState<UserDisplayData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.users.list(PAGE_SIZE);
      // Transform backend User to frontend User format
      const transformedUsers: UserDisplayData[] = data.map((user) => ({
        id: user.id,
        name: user.display_name || user.email.split("@")[0],
        email: user.email,
        agency: user.agency_id || "N/A", // TODO: Fetch agency name from agency_id
        role: user.is_superuser ? "Administrator" : "User",
        department: "N/A", // Not in backend model
        cases: 0, // TODO: Fetch from user cases endpoint
        last_active: user.updated_at,
        status: user.is_active ? "Active" : "Inactive",
      }));
      setUsers(transformedUsers);
      setHasMore(data.length === PAGE_SIZE);
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 401) {
          logout();
        } else {
          setError(err.detail || "Failed to load users");
        }
      } else {
        setError("Failed to load users");
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, logout]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleNextPage = () => {
    if (!loading && hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (!loading && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };


  // Define columns for the Users table
  const columns: ColumnDef<typeof users[0]>[] = [
    {
      key: "name",
      header: "Name",
      render: (item) => (
        <div className="text-xs font-medium text-foreground">{item.name}</div>
      ),
      sortValue: (item) => item.name,
    },
    {
      key: "email",
      header: "Email",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.email}</div>
      ),
      sortValue: (item) => item.email,
    },
    {
      key: "agency",
      header: "Agency",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.agency}</div>
      ),
      sortValue: (item) => item.agency,
    },
    {
      key: "role",
      header: "Role",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.role}</div>
      ),
      sortValue: (item) => item.role,
    },
    {
      key: "department",
      header: "Department",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.department}</div>
      ),
      sortValue: (item) => item.department,
    },
    {
      key: "cases",
      header: "Cases",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.cases}</div>
      ),
      sortValue: (item) => item.cases,
    },
    {
      key: "last_active",
      header: "Last Active",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {new Date(item.last_active).toLocaleDateString()}
        </div>
      ),
      sortValue: (item) => new Date(item.last_active).getTime(),
    },
    {
      key: "status",
      header: "Status",
      render: (item) => (
        <StatusBadge status={item.status} />
      ),
      sortValue: (item) => item.status,
    },
    {
      key: "actions",
      header: "Actions",
      sortable: false,
      render: (item) => (
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-3"
          onClick={() => {
            // TODO: Navigate to user details or open modal
            console.log("View user:", item.id);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  // Define filters (for search functionality)
  const filters: FilterOption[] = [
    { label: "Agency", placeholder: "All Agencies" },
    { label: "Role", placeholder: "All Roles" },
    { label: "Status", placeholder: "All Statuses" },
    { label: "Department", placeholder: "All Departments" },
  ];

  // Handle new user creation
  const handleNewUser = () => {
    // TODO: Implement new user modal or navigation
    console.log("Create new user");
  };

  // Handle export
  const handleExport = () => {
    // TODO: Implement export functionality
    console.log("Export users");
  };

  // Action buttons for the header
  const actionButtons = (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        className="h-9 px-4"
      >
        <Download className="h-4 w-4 mr-2" />
        Export
      </Button>
      <Button
        size="sm"
        onClick={handleNewUser}
        className="h-9 px-4"
      >
        <Plus className="h-4 w-4 mr-2" />
        New User
      </Button>
    </>
  );

  return (
    <DataTable
      title="User Management"
      subtitle="Manage system access and permissions"
      columns={columns}
      data={users}
      loading={loading}
      error={error}
      filters={filters}
      searchPlaceholder="Search users..."
      onNextPage={handleNextPage}
      onPreviousPage={handlePreviousPage}
      hasNextPage={hasMore}
      hasPreviousPage={currentPage > 1}
      currentPage={currentPage}
      itemsPerPage={PAGE_SIZE}
      actionButtons={actionButtons}
      totalCount={undefined}
      entityName="users"
    />
  );
}


